/**
 * Main App component for the Contractor view.
 *
 * Phase 1: Simple task list with API fetching
 * Phase 2: Task detail with start/stop/complete
 * Phase 3: Time tracking with offline support
 * Phase 4: WebSocket real-time updates (my_tasks channel only)
 *
 * Security note: This view shows SO numbers only, never customer names.
 * The API enforces this, but we also don't render customerName even if present.
 */

import { useState, useCallback, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from './components/AppShell';
import { TaskList } from './components/TaskList';
import { TaskDetailView } from './components/TaskDetailView';
import { TimeEntryList } from './components/TimeEntryList';
import { WebSocketProvider, useWebSocketContext } from './context/WebSocketContext';

// React Query client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 3,
      staleTime: 30_000, // 30 seconds
    },
  },
});

type Tab = 'tasks' | 'time';
type View = 'list' | 'detail';

function AppContent() {
  const [activeTab, setActiveTab] = useState<Tab>('tasks');
  const [view, setView] = useState<View>('list');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showNewTaskToast, setShowNewTaskToast] = useState(false);

  // WebSocket context for real-time updates
  const { hasNewAssignment, clearNewAssignment } = useWebSocketContext();

  // Show toast when new task assigned via WebSocket
  useEffect(() => {
    if (hasNewAssignment) {
      setShowNewTaskToast(true);
      clearNewAssignment();

      // Auto-hide toast after 5 seconds
      const timer = setTimeout(() => {
        setShowNewTaskToast(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [hasNewAssignment, clearNewAssignment]);

  const handleTaskSelect = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setView('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setView('list');
    setSelectedTaskId(null);
  }, []);

  const handleTabChange = useCallback((tab: Tab) => {
    setActiveTab(tab);
    // Reset to list view when switching tabs
    if (view === 'detail') {
      setView('list');
      setSelectedTaskId(null);
    }
    // Clear toast when navigating to tasks tab
    if (tab === 'tasks') {
      setShowNewTaskToast(false);
    }
  }, [view]);

  // Determine title based on view and tab
  const getTitle = () => {
    if (view === 'detail') return 'Task Details';
    if (activeTab === 'time') return 'Time Entries';
    return 'My Tasks';
  };

  // Don't show bottom nav when in detail view
  const showBottomNav = view === 'list';

  return (
    <>
      <AppShell
        title={getTitle()}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        showBottomNav={showBottomNav}
      >
        {view === 'detail' ? (
          <TaskDetailView taskId={selectedTaskId} onBack={handleBackToList} />
        ) : activeTab === 'tasks' ? (
          <TaskList onTaskSelect={handleTaskSelect} />
        ) : (
          <TimeEntryList />
        )}
      </AppShell>

      {/* New task assignment toast notification */}
      {showNewTaskToast && (
        <div className="fixed bottom-20 left-4 right-4 z-50 safe-bottom">
          <button
            type="button"
            onClick={() => {
              setShowNewTaskToast(false);
              setActiveTab('tasks');
              setView('list');
            }}
            className="w-full bg-accent text-white px-4 py-3 rounded-xl shadow-lg flex items-center justify-between hover:bg-accent-bright transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 1v8M4 5l4 4 4-4M2 12h12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="font-medium">New task assigned</span>
            </div>
            <span className="text-white/70 text-sm">View</span>
          </button>
        </div>
      )}
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <WebSocketProvider>
        <AppContent />
      </WebSocketProvider>
    </QueryClientProvider>
  );
}
